// mattermost-command-body-parser.ts — split out of
// mattermost-command-parser.ts (file-size convention) — pure text-to-kind/
// payload parsing for the body AFTER "@avatar ", once the acceptance filter
// (actor/channel/thread/time/bot/quote/fence) has already passed. No
// Mattermost types here on purpose — this module only knows about strings.
export type CommandKind =
  | "status"
  | "approve"
  | "pause"
  | "resume"
  | "redirect"
  | "answer"
  | "revert"
  | "abort"
  | "abort_confirm";

export type ParsedCommandBody =
  | { kind: CommandKind; payload: Record<string, unknown> }
  | "missing_request_id"
  | "legacy_answer_form"
  | undefined;

/** Parses the command body (text AFTER "@avatar ") into a kind + payload, or undefined if unrecognized. */
export function parseCommandBody(body: string): ParsedCommandBody {
  const [head, ...restParts] = body.split(/\s+/);

  switch (head) {
    case "status":
      return { kind: "status", payload: {} };
    case "pause":
      return { kind: "pause", payload: {} };
    case "resume":
      return { kind: "resume", payload: {} };
    case "revert": {
      const ticket = restParts[0];
      if (!ticket) return undefined;
      return { kind: "revert", payload: { ticket } };
    }
    case "approve": {
      const gate = restParts[0];
      const requestId = restParts[1];
      if (!gate) return undefined;
      if (!requestId) return "missing_request_id";
      return { kind: "approve", payload: { gate, request_id: requestId } };
    }
    case "abort": {
      if (restParts[0] === "confirm" && restParts[1]) {
        return { kind: "abort_confirm", payload: { challenge_id: restParts[1] } };
      }
      if (restParts.length === 0) return { kind: "abort", payload: {} };
      return undefined;
    }
    default:
      break;
  }

  // "redirect: ..." and "answer <question-id>: ..." use a colon-delimited
  // free-text tail — matched against the RAW body, not the whitespace-split
  // head, since the text after ':' may contain arbitrary spaces.
  const redirectMatch = /^redirect:\s*(.+)$/s.exec(body);
  if (redirectMatch?.[1] !== undefined) {
    return { kind: "redirect", payload: { text: redirectMatch[1].trim() } };
  }
  // M1 (backlog 2026-10-06): the legacy two-token form (`answer <ticket>
  // <req>: ...`) is REJECTED by name — the controller always refused its
  // payload, so accepting it into the inbox only hid the reason from chat.
  // Mirrors the CLI refusing `autopilot answer --request`. Tokens exclude ':'
  // (review 261006 M1): with `\S+`, `answer Q-1: 10:30` read as legacy.
  if (/^answer\s+[^\s:]+\s+[^\s:]+:\s*.+$/s.test(body)) return "legacy_answer_form";
  // B4 (plan 20260915-1650 phase-04): `answer <question-id>: <giá trị>` — một
  // token trước dấu ':'. Thứ tự QUAN TRỌNG: dạng cũ thử trước, nên một câu trả
  // lời hai token không bao giờ bị đọc nhầm thành question_id. Payload này là
  // hình dạng interview-answer-directive-processing.ts nhận.
  const questionAnswerMatch = /^answer\s+([^\s:]+):\s*(.+)$/s.exec(body);
  if (questionAnswerMatch) {
    const [, questionId, value] = questionAnswerMatch;
    return { kind: "answer", payload: { question_id: questionId, value: value?.trim() } };
  }

  return undefined;
}
